/**
 * Node-side orchestration for DOM snapshots: what to capture, how much of it,
 * and where the baseline lives.
 *
 * The walker itself is `dom-capture.ts` and runs in the browser. This module
 * never touches the DOM; it decides the policy and owns the files.
 */

import { readFileSync, writeFileSync, existsSync, mkdirSync } from 'node:fs';
import { dirname } from 'node:path';
import { captureDomSnapshot, type DomSnapshot, type CaptureOptions } from './dom-capture.js';

/**
 * Computed properties worth recording.
 *
 * Curated rather than exhaustive: every property here can change what a
 * screenshot looks like, and nothing here is a derived duplicate of something
 * else in the list. Margin and padding are kept as longhands so a report can
 * say `margin-top 0px → 24px` instead of the far less useful
 * `margin "0px" → "24px 0px 0px"`.
 */
export const STYLE_PROPS: readonly string[] = [
  'display',
  'position',
  'color',
  'background-color',
  'background-image',
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
  'border-color',
  'border-style',
  'border-radius',
  'box-shadow',
  'opacity',
  'transform',
  'margin-top',
  'margin-right',
  'margin-bottom',
  'margin-left',
  'padding-top',
  'padding-right',
  'padding-bottom',
  'padding-left',
  'font-family',
  'font-size',
  'font-weight',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-decoration-line',
  'text-transform',
  'flex-direction',
  'justify-content',
  'align-items',
  'gap',
  'z-index',
  'overflow',
];

/**
 * Initial values, omitted from the snapshot where they match.
 *
 * This is what keeps snapshots small — most elements are at the initial value
 * for most of these, so a typical node stores three or four entries instead of
 * thirty-seven. `display` is deliberately absent: its initial value depends on
 * the tag, and it is high-signal enough to always be worth the bytes.
 */
export const STYLE_DEFAULTS: Readonly<Record<string, string>> = {
  position: 'static',
  'background-color': 'rgba(0, 0, 0, 0)',
  'background-image': 'none',
  'border-top-width': '0px',
  'border-right-width': '0px',
  'border-bottom-width': '0px',
  'border-left-width': '0px',
  'border-style': 'none',
  'border-radius': '0px',
  'box-shadow': 'none',
  opacity: '1',
  transform: 'none',
  'margin-top': '0px',
  'margin-right': '0px',
  'margin-bottom': '0px',
  'margin-left': '0px',
  'padding-top': '0px',
  'padding-right': '0px',
  'padding-bottom': '0px',
  'padding-left': '0px',
  'text-decoration-line': 'none',
  'flex-direction': 'row',
  'justify-content': 'normal',
  'align-items': 'normal',
  gap: 'normal',
  'z-index': 'auto',
  overflow: 'visible',
};

/**
 * Properties that inherit, recorded only where they differ from the parent.
 *
 * Without this, restyling the body font would report an identical change on
 * every one of its descendants — technically true and completely unreadable.
 * With it, the change appears once, on the element that actually caused it.
 */
export const INHERITED_PROPS: readonly string[] = [
  'color',
  'font-family',
  'font-size',
  'font-weight',
  'line-height',
  'letter-spacing',
  'text-align',
  'text-transform',
];

/** Matches `maestro-analytics`' body cap — the house default for captured payloads. */
export const DEFAULT_DOM_MAX_BYTES = 1024 * 1024;
export const DEFAULT_DOM_MAX_NODES = 1500;
export const DEFAULT_DOM_MAX_TEXT = 200;

export interface DomCaptureConfig {
  readonly maxNodes?: number;
  readonly maxBytes?: number;
  readonly maxText?: number;
}

/** The subset of a Playwright Page or Locator this module needs. */
export interface EvaluateTarget {
  evaluate<R, A>(fn: (arg: A) => R, arg: A): Promise<R>;
}

/** A Locator, for the mask and capture-box cases. */
export interface BoundedTarget {
  boundingBox(): Promise<{ x: number; y: number; width: number; height: number } | null>;
}

export interface CaptureRequest {
  /** Page or Locator — whatever the assertion was made against. */
  readonly target: EvaluateTarget;
  /** Set when asserting against a locator, so the capture box can be framed. */
  readonly bounded?: BoundedTarget | null;
  readonly maskLocators?: readonly BoundedTarget[];
  readonly fullPage?: boolean;
  readonly config?: DomCaptureConfig;
}

/** Resolve mask locators to boxes, skipping any that are not currently laid out. */
async function resolveMaskBoxes(
  locators: readonly BoundedTarget[],
): Promise<[number, number, number, number][]> {
  const boxes: [number, number, number, number][] = [];
  for (const locator of locators) {
    try {
      const box = await locator.boundingBox();
      if (box) boxes.push([box.x, box.y, box.width, box.height]);
    } catch {
      // A mask that cannot be resolved just does not mask anything.
    }
  }
  return boxes;
}

/**
 * Capture a DOM snapshot of whatever the assertion is about to compare.
 *
 * Returns null rather than throwing on any failure. A visual assertion must
 * behave identically whether or not DOM capture worked.
 */
export async function captureDom(request: CaptureRequest): Promise<DomSnapshot | null> {
  const config = request.config ?? {};
  try {
    const maskBoxes = request.maskLocators?.length
      ? await resolveMaskBoxes(request.maskLocators)
      : [];

    let viewportBox: [number, number, number, number] | null = null;
    if (request.bounded) {
      const box = await request.bounded.boundingBox();
      if (box) viewportBox = [box.x, box.y, box.width, box.height];
    }

    const options: CaptureOptions = {
      styleProps: STYLE_PROPS,
      defaults: STYLE_DEFAULTS,
      inherited: INHERITED_PROPS,
      maxNodes: config.maxNodes ?? DEFAULT_DOM_MAX_NODES,
      maxText: config.maxText ?? DEFAULT_DOM_MAX_TEXT,
      maskBoxes,
      viewportBox,
      fullPage: request.fullPage === true,
    };

    return await request.target.evaluate(captureDomSnapshot, options);
  } catch {
    return null;
  }
}

export interface SerializedSnapshot {
  readonly json: string;
  readonly truncatedBytes: boolean;
}

/**
 * Serialize a snapshot, dropping nodes from the end until it fits.
 *
 * Nodes are in document order, so trimming from the end keeps the top of the
 * page — the part a screenshot is most likely to be about. The caller surfaces
 * `truncatedBytes` as a visible badge; silently shipping a partial snapshot
 * would make the diff lie about what was compared.
 */
export function serializeSnapshot(
  snapshot: DomSnapshot,
  maxBytes: number = DEFAULT_DOM_MAX_BYTES,
): SerializedSnapshot {
  let json = JSON.stringify(snapshot);
  if (Buffer.byteLength(json, 'utf-8') <= maxBytes) {
    return { json, truncatedBytes: false };
  }

  const nodes = snapshot.nodes.slice();
  let dropped = 0;
  while (nodes.length > 0 && Buffer.byteLength(json, 'utf-8') > maxBytes) {
    // Drop in blocks: re-serializing once per node is quadratic on a big page.
    const block = Math.max(1, Math.floor(nodes.length * 0.1));
    nodes.splice(-block, block);
    dropped += block;
    json = JSON.stringify({
      ...snapshot,
      nodes,
      truncatedNodes: snapshot.truncatedNodes + dropped,
    });
  }

  return { json, truncatedBytes: true };
}

/** The DOM baseline sits beside the image baseline: `home.png` → `home.dom.json`. */
export function domBaselinePath(screenshotPath: string): string {
  return screenshotPath.replace(/\.[A-Za-z0-9]+$/, '') + '.dom.json';
}

/** Read a committed DOM baseline. Returns null when there is not one yet. */
export function readDomBaseline(path: string): DomSnapshot | null {
  try {
    if (!existsSync(path)) return null;
    const parsed: unknown = JSON.parse(readFileSync(path, 'utf-8'));
    return isDomSnapshot(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Write a DOM baseline, creating its directory if Playwright has not yet. */
export function writeDomBaseline(path: string, json: string): boolean {
  try {
    mkdirSync(dirname(path), { recursive: true });
    writeFileSync(path, json, 'utf-8');
    return true;
  } catch {
    return false;
  }
}

// SAFETY: read back from a file a previous run wrote, which a human may have
// since edited or a merge may have mangled. Shape-checked before use.
export function isDomSnapshot(value: unknown): value is DomSnapshot {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.version === 'string' &&
    Array.isArray(v.nodes) &&
    Array.isArray(v.captureBox) &&
    (v.captureBox as unknown[]).length === 4
  );
}
